import {
	IDrawerDataListColumn,
	PaginatedDrawerAutoDataList,
	laravelPaginatedListFetchConfig,
	useHasPermission,
	useLaravelCrudEntity,
} from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { industryService as sharedIndustryService } from "../../../shared/api/industryService";
import { PermissionName } from "../../../shared/types/permission";
import { industryService } from "../shared/api/industryService";
import { IndustryFormValues, IndustryIndexResource, IndustrySaveRequest, UserOptionsResource } from "../shared/types";
import { IndustryCreateDrawer, IndustryUpdateDrawer } from "./IndustryDrawer";
import { IndustryListContextMenu } from "./IndustryListContextMenu";

export const IndustryList: FC = () => {
	const { t } = useTranslation();

	const crudOperations = useLaravelCrudEntity(
		industryService,
		{
			entityName: t("Industry"),
			messages: {
				create: t("Industry was saved."),
				update: t("Industry was saved."),
				delete: t("Industry was deleted."),
			},
		},
		[],
		[sharedIndustryService.quickFilterKey],
	);

	const dataListColumns: IDrawerDataListColumn<IndustryIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Name"),
				valueKey: "name",
				type: "value",
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: ({ id }, openDrawer) => (
					<IndustryListContextMenu
						industryId={id}
						handleEdit={openDrawer}
						handleDelete={() => crudOperations.deleteEntity(id)}
					/>
				),
			},
		],
		[crudOperations, t],
	);

	const canCreate = useHasPermission<PermissionName>(["industry.create"]);
	const canUpdate = useHasPermission<PermissionName>(["industry.update"]);

	return (
		<PaginatedDrawerAutoDataList
			canUpdate={canUpdate}
			canCreate={canCreate}
			initialSearch={{
				search: "",
				searchFields: [{ name: "name", label: t("Name") }],
			}}
			translations={{
				title: t("Industries"),
				empty: t("No industries available"),
			}}
			renderCreateDrawer={(drawerProps) => (
				<IndustryCreateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.createEntity(mapToRequest(entityFormState))}
				/>
			)}
			renderUpdateDrawer={(drawerProps) => (
				<IndustryUpdateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.updateEntity(drawerProps.id, mapToRequest(entityFormState))}
				/>
			)}
			columns={dataListColumns}
			listFetchConfig={laravelPaginatedListFetchConfig(industryService.listKey, industryService.index)}
		/>
	);
};

function mapToRequest(values: IndustryFormValues): IndustrySaveRequest {
	return {
		...values,
		userIds: values.users.map((user: UserOptionsResource) => user.id) ?? [],
	};
}
